import { useEffect, useState } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import App from '@/App'
import { useLoadProject } from '@/hooks/useLoadProject'
import { useTabsStore } from '@/stores/tabsStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { morphToast } from '@/components/ui/Toast'
import { useConflictStore } from '@/stores/conflictStore'
import { ConflictResolverModal } from '@/components/conflict/ConflictResolverModal'
import { connectProjectRealtime } from '@/services/realtime/socket'

export default function EditorPage() {
  const { workspaceSlug, pid } = useParams<{ workspaceSlug: string; pid: string }>()
  const projectId = Number(pid)

  if (!pid || Number.isNaN(projectId) || !workspaceSlug) {
    return <Navigate to="/" replace />
  }

  return <WorkspaceLoader workspaceSlug={workspaceSlug} projectId={projectId} />
}

// ─── Step 1: load workspace by slug, then mount editor ───────────────────────

interface WorkspaceLoaderProps {
  workspaceSlug: string
  projectId: number
}

function WorkspaceLoader({ workspaceSlug, projectId }: WorkspaceLoaderProps) {
  const { t } = useTranslation()
  const loadWorkspaceBySlug = useWorkspaceStore((s) => s.loadWorkspaceBySlug)
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const navigate = useNavigate()

  const [wsStatus, setWsStatus] = useState<'loading' | 'ready' | 'error'>(() =>
    // If the workspace is already loaded with the correct slug, skip the fetch
    currentWorkspace?.slug === workspaceSlug ? 'ready' : 'loading',
  )
  const [wsError, setWsError] = useState<string | null>(null)

  useEffect(() => {
    if (currentWorkspace?.slug === workspaceSlug) {
      setWsError(null)
      setWsStatus('ready')
      return
    }
    setWsStatus('loading')
    loadWorkspaceBySlug(workspaceSlug)
      .then(() => {
        setWsError(null)
        setWsStatus('ready')
      })
      .catch((err: unknown) => {
        const status = (err as { status?: number })?.status
        if (status === 404) {
          setWsError(t('workspace.notFound', 'Workspace não encontrado.'))
        } else if (status === 403) {
          setWsError(t('workspace.forbidden', 'Você não tem permissão para acessar este workspace.'))
        } else {
          setWsError(t('common.error', 'Erro ao carregar workspace.'))
        }
        setWsStatus('error')
      })
  }, [workspaceSlug, currentWorkspace?.slug, loadWorkspaceBySlug, t])

  if (wsStatus === 'loading') {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={styles.label}>{t('editor.loadingWorkspace', 'Carregando workspace...')}</p>
      </div>
    )
  }

  if (wsStatus === 'error') {
    return (
      <div style={styles.center}>
        <p style={{ ...styles.label, color: 'var(--color-danger, #f87171)' }}>{wsError}</p>
        <button style={styles.button} onClick={() => navigate('/workspaces', { replace: true })}>
          {t('workspace.goToList', 'Ver workspaces')}
        </button>
      </div>
    )
  }

  return (
    <EditorLoader
      projectId={projectId}
      workspaceSlug={workspaceSlug}
      workspaceId={currentWorkspace!.id}
    />
  )
}

// ─── Step 2: load project, validate ownership, render editor ─────────────────

interface EditorLoaderProps {
  projectId: number
  workspaceSlug: string
  workspaceId: number
}

function EditorLoader({ projectId, workspaceSlug, workspaceId }: EditorLoaderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const {
    status,
    error,
    errorStatus,
    errorCode,
    project,
    pendingBackup,
    recoverFromBackup,
    dismissBackup,
  } = useLoadProject(projectId)
  const [backupDismissed, setBackupDismissed] = useState(false)
  const tabCount = useTabsStore((s) => s.tabs.length)
  const tabs = useTabsStore((s) => s.tabs)
  const closeTab = useTabsStore((s) => s.closeTab)
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const [missingProjectHandled, setMissingProjectHandled] = useState(false)
  const conflictActive = useConflictStore((s) => s.active)
  const conflictStatus = useConflictStore((s) => s.status)
  const conflictMergeResult = useConflictStore((s) => s.mergeResult)
  const conflictApply = useConflictStore((s) => s.apply)
  const conflictCancel = useConflictStore((s) => s.cancel)

  useEffect(() => {
    if (status !== 'ready') return
    const realtime = connectProjectRealtime(projectId)
    return () => realtime.close()
  }, [projectId, status])

  useEffect(() => {
    setBackupDismissed(false)
    setMissingProjectHandled(false)
  }, [projectId])

  // Validate that the loaded project belongs to this workspace
  useEffect(() => {
    if (!project || !currentWorkspace) return
    if (currentWorkspace.id === project.workspace && currentWorkspace.slug !== workspaceSlug) {
      navigate(`/workspace/${currentWorkspace.slug}/projects/${project.id}/editor`, { replace: true })
      return
    }
    if (project.workspace === workspaceId) return
    if (currentWorkspace?.id === project.workspace) {
      navigate(`/workspace/${currentWorkspace.slug}/projects/${project.id}/editor`, { replace: true })
      return
    }
    if (currentWorkspace?.id === workspaceId) return
    navigate(`/workspace/${workspaceSlug}/projects`, { replace: true })
  }, [project, workspaceId, workspaceSlug, navigate, currentWorkspace])

  // When all tabs are closed, return to project list
  useEffect(() => {
    if (status !== 'ready') return
    if (tabCount > 0) return
    navigate(`/workspace/${workspaceSlug}/projects`, { replace: true })
  }, [status, tabCount, workspaceSlug, navigate])

  useEffect(() => {
    if (status !== 'error' || missingProjectHandled) return
    const projectMissing = errorStatus === 404 || errorCode === 'NOT_FOUND'
    if (!projectMissing) return

    const currentTab = tabs.find((tab) => Number(tab.projectId) === projectId)
    const remainingTabs = tabs.filter((tab) => Number(tab.projectId) !== projectId)
    const fallbackTab = remainingTabs.find((tab) => Number(tab.projectId) > 0) ?? null
    const fallbackWorkspaceSlug = fallbackTab?.workspaceId != null
      ? workspaces.find((workspace) => workspace.id === fallbackTab.workspaceId)?.slug
      : currentWorkspace?.slug
    const fallbackProjectId = fallbackTab ? Number(fallbackTab.projectId) : null

    setMissingProjectHandled(true)
    morphToast.error(t('editor.projectDeleted', 'Este projeto foi apagado e a aba foi fechada.'))
    if (currentTab) {
      closeTab(currentTab.id)
    }

    if (fallbackTab && fallbackWorkspaceSlug && fallbackProjectId && fallbackProjectId > 0) {
      navigate(`/workspace/${fallbackWorkspaceSlug}/projects/${fallbackProjectId}/editor`, { replace: true })
      return
    }

    navigate(`/workspace/${workspaceSlug}/projects`, { replace: true })
  }, [
    status,
    missingProjectHandled,
    errorStatus,
    errorCode,
    tabs,
    projectId,
    workspaces,
    currentWorkspace,
    closeTab,
    navigate,
    workspaceSlug,
    t,
  ])

  if (status === 'loading' || status === 'idle') {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={styles.label}>{t('editor.loading', 'Carregando projeto...')}</p>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div style={styles.center}>
        <p style={{ ...styles.label, color: 'var(--color-danger, #f87171)' }}>
          {error ?? t('common.error', 'Erro ao carregar projeto')}
        </p>
        <button style={styles.button} onClick={() => window.location.reload()}>
          {t('common.retry', 'Tentar novamente')}
        </button>
      </div>
    )
  }

  return (
    <>
      {pendingBackup && !backupDismissed && (
        <BackupBanner
          cachedAt={pendingBackup.cachedAt}
          onRecover={() => { recoverFromBackup(); setBackupDismissed(true) }}
          onDiscard={async () => { await dismissBackup(); setBackupDismissed(true) }}
        />
      )}
      {conflictActive && conflictStatus === 'modal' && conflictMergeResult && (
        <ConflictResolverModal
          open
          mergeResult={conflictMergeResult}
          onCancel={conflictCancel}
          onApply={(resolutionSet) => { void conflictApply(resolutionSet) }}
          onDiscardMine={conflictCancel}
        />
      )}
      <App />
    </>
  )
}

interface BackupBannerProps {
  cachedAt: string
  onRecover: () => void
  onDiscard: () => Promise<void>
}

function BackupBanner({ cachedAt, onRecover, onDiscard }: BackupBannerProps) {
  const { t } = useTranslation()
  const when = new Date(cachedAt).toLocaleString()

  return (
    <div style={{ ...styles.banner, background: '#1e3a8a', borderBottomColor: '#1d4ed8' }}>
      <span style={{ ...styles.bannerText, color: '#dbeafe' }}>
        {t(
          'editor.backupAvailable',
          `Backup local mais novo encontrado (${when}) — provavelmente uma falha de salvamento anterior.`,
        )}
      </span>
      <div style={styles.bannerActions}>
        <button
          style={{ ...styles.bannerBtn, ...styles.bannerBtnGhost, color: '#dbeafe', borderColor: '#1d4ed8' }}
          onClick={() => void onDiscard()}
        >
          {t('editor.backupDiscard', 'Descartar')}
        </button>
        <button
          style={{ ...styles.bannerBtn, background: '#2563eb', color: '#fff' }}
          onClick={onRecover}
        >
          {t('editor.backupRecover', 'Recuperar')}
        </button>
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  center: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    flex: 1,
    gap: 16,
    background: 'var(--bg-primary, #0f1117)',
    color: 'var(--text-primary, #e4e7ec)',
  },
  spinner: {
    width: 36,
    height: 36,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  label: {
    fontSize: 14,
    color: 'var(--text-dim, #9ca3af)',
    margin: 0,
  },
  button: {
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 20px',
    fontSize: 13,
    cursor: 'pointer',
  },
  banner: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    gap: 12,
    padding: '10px 20px',
    background: '#78350f',
    borderBottom: '1px solid #92400e',
    zIndex: 1000,
  },
  bannerText: {
    fontSize: 13,
    color: '#fde68a',
  },
  bannerActions: {
    display: 'flex' as const,
    gap: 8,
    flexShrink: 0,
  },
  bannerBtn: {
    borderRadius: 6,
    padding: '4px 14px',
    fontSize: 12,
    cursor: 'pointer',
    border: 'none',
  },
  bannerBtnGhost: {
    background: 'transparent',
    color: '#fde68a',
    border: '1px solid #92400e',
  } as React.CSSProperties,
  bannerBtnPrimary: {
    background: '#d97706',
    color: '#fff',
  } as React.CSSProperties,
}
